<template>
  <section class="one-off-band" :class="{ 'is-dark': dark }">
    <div class="container">
      <div class="one-off-band-inner">
        <div class="one-off-band-copy" data-aos="fade-right">
          <h2 class="one-off-band-title">{{ t('Prefer not to pay monthly?') }}</h2>
          <p class="one-off-band-note">{{ t('One payment for the design and build, with no minimum contract.') }}</p>
        </div>
        <app-button
          :href="href"
          tone="dark"
          variant="ghost"
          icon="arrow-right"
          icon-after
          block
          data-aos="fade-left"
        >
          {{ t('Check out our One-Time Web Design Plans') }}
        </app-button>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

/* Cross-sell band that sits under a monthly plan table. Both grounds share one
   skeleton - a flat brand colour, a masked hairline lattice, and a subtle tint -
   and differ only in what they are washed with: primary blue by default,
   $ground-dark for `dark`. The lattice is masked so it is strongest behind the
   copy and clears toward the right edge, where the button lands. */
.one-off-band {
  background: $primary;
  color: $on-dark-strong;
  overflow: hidden;
  padding: 3.25rem 0;
  position: relative;

  &::before {
    /* A soft tint for depth, then the lattice drawn as two hairline gradients
       the way the services section builds its blueprint ground. */
    background:
      radial-gradient(56rem 28rem at 4% -10%, rgba($accent-blue, .5), transparent 72%),
      linear-gradient($on-dark-fill 1px, transparent 1px),
      linear-gradient(90deg, $on-dark-fill 1px, transparent 1px);
    background-size: auto, 3.5rem 3.5rem, 3.5rem 3.5rem;
    content: '';
    inset: 0;
    mask-image: linear-gradient(90deg, #000, rgb(0 0 0 / 30%) 55%, rgb(0 0 0 / 0%) 84%);
    pointer-events: none;
    position: absolute;
    -webkit-mask-image: linear-gradient(90deg, #000, rgb(0 0 0 / 30%) 55%, rgb(0 0 0 / 0%) 84%);
  }
}

/* Dark ground, matched to section#feature. No tint on this one: a colour wash
   over $ground-dark reads as a blue cast rather than depth, so the lattice
   carries the pattern alone. Everything else, including the button, is
   inherited. */
.one-off-band.is-dark {
  background: $ground-dark;
  color: $on-dark-body;

  &::before {
    background:
      linear-gradient($on-dark-fill 1px, transparent 1px),
      linear-gradient(90deg, $on-dark-fill 1px, transparent 1px);
    background-size: 3.5rem 3.5rem, 3.5rem 3.5rem;
  }

  .one-off-band-note {
    color: $on-dark-body;
  }
}

.one-off-band-inner {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem 2.5rem;
  justify-content: space-between;
  position: relative;
  z-index: 1;
}

.one-off-band-title {
  color: $on-dark-strong;
  font-size: 1.6rem;
  line-height: 1.2;
  margin: 0 0 0.5rem;

  @media (min-width: 768px) {
    font-size: 2rem;
  }
}

.one-off-band-note {
  color: $on-dark-body;
  font-size: 1rem;
  line-height: 1.5;
  margin: 0;
  max-width: 34rem;
}

/* The band's CTA used to be a bespoke ghost pill declared here, the second of
   the four CTA languages on the site. It is AppButton's `ghost` variant now: it
   inherits the button radius, the lift and the arrow nudge, so it matches the
   filled CTA it sits beside instead of being its own one-off shape. */
</style>

<script>
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { library as FontAwesomeLibrary } from '@fortawesome/fontawesome-svg-core';
import { useI18n } from 'vue-i18n';
import AppButton from '@/Components/AppButton.vue';

FontAwesomeLibrary.add(faArrowRight);

export default {
  components: {
    AppButton
  },

  props: {
    /* Where the band points. A hash when the one-off plans are on the same
       page, or a route plus hash when they live on another one. */
    href: {
      type: String,
      required: true
    },
    /* $ground-dark ground instead of the primary one, for a page that already opens on
       blue. */
    dark: {
      type: Boolean,
      default: false
    }
  },

  setup() {
    const { t } = useI18n({
      messages: {
        'en-MY': {
          'Prefer not to pay monthly?': 'Prefer not to pay monthly?',
          'One payment for the design and build, with no minimum contract.': 'One payment for the design and build, with no minimum contract.',
          'Check out our One-Time Web Design Plans': 'Check out our One-Time Web Design Plans'
        },
        'zh-MY': {
          'Prefer not to pay monthly?': '不想每月付费？',
          'One payment for the design and build, with no minimum contract.': '一次付费完成设计与开发，没有最短合约。',
          'Check out our One-Time Web Design Plans': '查看我们的一次性网页设计配套'
        },
        'ms-MY': {
          'Prefer not to pay monthly?': 'Tidak mahu bayar bulanan?',
          'One payment for the design and build, with no minimum contract.': 'Satu bayaran untuk reka bentuk dan pembinaan, tanpa kontrak minimum.',
          'Check out our One-Time Web Design Plans': 'Lihat pelan reka bentuk web sekali bayar kami'
        }
      }
    });

    return { t };
  }
};
</script>
